import { CustomQuestionFieldType } from 'src/graphql/types/customQuestions'

export interface PublicQuestionOption {
  customQuestionOptionId: string
  label: string
  order: number
}

/**
 * Public-safe projection of a template question served on the
 * unauthenticated short form (BasicJobListing.publicQuestions).
 */
export interface PublicTemplateQuestion {
  jobListingTemplateQuestionId: string
  label: string
  fieldType: CustomQuestionFieldType
  isRequired: boolean
  placeholder?: string | null
  helpText?: string | null
  order: number
  options: PublicQuestionOption[]
}

/**
 * In-form answer shapes: SHORT_TEXT/LONG_TEXT hold the text, BOOLEAN holds
 * 'true' | 'false' (radio card values), SINGLE_SELECT holds one option id,
 * MULTI_SELECT holds an array of option ids. Untouched questions are absent
 * or undefined.
 */
export type QuestionAnswerValue = string | string[] | undefined

export type QuestionAnswers = Record<string, QuestionAnswerValue>

export interface BasicApplicantQuestionResponseInput {
  jobListingTemplateQuestionId: string
  valueShortText?: string
  valueLongText?: string
  valueBoolean?: boolean
  selectedOptionIds?: string[]
}

/** Questions render in the template's drag order. */
export const sortPublicQuestions = (
  questions: PublicTemplateQuestion[]
): PublicTemplateQuestion[] => [...questions].sort((a, b) => a.order - b.order)

// Mirror the server-side limits in validateBasicApplicantQuestionResponses —
// exceeding them there rejects the whole submit with a generic error, so the
// form must catch oversized answers client-side first.
export const SHORT_TEXT_MAX_LENGTH = 500
export const LONG_TEXT_MAX_LENGTH = 5000

/** Returns a field-level error message when a text answer exceeds the server limit. */
export const getAnswerLengthError = (
  question: PublicTemplateQuestion,
  value: QuestionAnswerValue
): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const maxLength =
    question.fieldType === CustomQuestionFieldType.SHORT_TEXT
      ? SHORT_TEXT_MAX_LENGTH
      : question.fieldType === CustomQuestionFieldType.LONG_TEXT
      ? LONG_TEXT_MAX_LENGTH
      : null
  if (maxLength === null || value.length <= maxLength) {
    return null
  }
  return `Answer must be ${maxLength} characters or fewer`
}

export const isQuestionAnswered = (
  question: PublicTemplateQuestion,
  value: QuestionAnswerValue
): boolean => {
  switch (question.fieldType) {
    case CustomQuestionFieldType.SHORT_TEXT:
    case CustomQuestionFieldType.LONG_TEXT:
      return typeof value === 'string' && value.trim() !== ''
    case CustomQuestionFieldType.BOOLEAN:
      return value === 'true' || value === 'false'
    case CustomQuestionFieldType.SINGLE_SELECT:
      return typeof value === 'string' && value !== ''
    case CustomQuestionFieldType.MULTI_SELECT:
      return Array.isArray(value) && value.length > 0
    default:
      return false
  }
}

/**
 * Maps answered questions into CreateBasicApplicantInput.questionResponses —
 * one entry per answered question, value bucket chosen by field type.
 * Unanswered (optional) questions are omitted entirely.
 */
export const buildQuestionResponses = (
  questions: PublicTemplateQuestion[],
  answers: QuestionAnswers
): BasicApplicantQuestionResponseInput[] =>
  questions
    .filter((question) =>
      isQuestionAnswered(
        question,
        answers[question.jobListingTemplateQuestionId]
      )
    )
    .map((question) => {
      const value = answers[question.jobListingTemplateQuestionId]
      const base = {
        jobListingTemplateQuestionId: question.jobListingTemplateQuestionId,
      }
      switch (question.fieldType) {
        case CustomQuestionFieldType.SHORT_TEXT:
          return { ...base, valueShortText: value as string }
        case CustomQuestionFieldType.LONG_TEXT:
          return { ...base, valueLongText: value as string }
        case CustomQuestionFieldType.BOOLEAN:
          return { ...base, valueBoolean: value === 'true' }
        case CustomQuestionFieldType.SINGLE_SELECT:
          return { ...base, selectedOptionIds: [value as string] }
        case CustomQuestionFieldType.MULTI_SELECT:
          return { ...base, selectedOptionIds: value as string[] }
        default:
          return base
      }
    })
